import { useCallback, useEffect, useState } from "react";
import {
  History,
  Download,
  Paperclip,
  Plus,
  Upload,
  Search,
  Settings2,
  ShieldAlert,
  Trash2,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Page, PageHeader, Empty } from "@/components/common";
import ImportDialog from "@/components/ImportDialog";
import { InventoryDetailsDialog } from "@/components/InventoryDetailsDialog";
import { InventoryDocs } from "@/components/InventoryDocs";
import { InventoryPhoto } from "@/components/InventoryPhoto";
import { InventoryPhotoPicker } from "@/components/InventoryPhotoPicker";
import { InventoryAttentionPanel } from "@/components/InventoryAttentionPanel";
import { InventoryDuplicatesDialog } from "@/components/InventoryDuplicatesDialog";
import { PrintButton } from "@/components/PrintButton";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  ["name", "Name"],
  ["category", "Category"],
  ["item_type", "Type (equipment/chemical/consumable)"],
  ["quantity", "Recorded quantity"],
  ["units", "Units"],
  ["location", "Storage location"],
  ["condition", "Condition"],
  ["availability", "Availability"],
  ["min_stock", "Minimum stock"],
  ["supplier", "Supplier"],
  ["purchase_info", "Purchase info"],
  ["date_added", "Date added"],
  ["youtube_url", "YouTube link"],
  ["last_inspected", "Last inspected"],
  ["next_inspection", "Next inspection"],
  ["hazard_info", "Hazard information"],
  ["hazcard_number", "CLEAPSS Hazcard Number"],
  ["storage_info", "Storage requirements"],
  ["safety_info", "Safety information"],
];

const NONE = "__none";
const NEW_CATEGORY = "__new_category";
const ALL_CATEGORIES = "__all_categories";
const MANAGE_CATEGORIES = "__manage_categories";
const BULK_CATEGORY = "__bulk_category";
const SORT_OPTIONS = [
  { value: "category", label: "Category, A–Z" },
  { value: "newest", label: "Newest added" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
];

function ManageCategoriesDialog({ onClose, onChanged }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [categories, setCategories] = useState([]);
  const load = () => {
    api
      .get("/inventory/categories/manage")
      .then(({ data }) => setCategories(data.categories))
      .catch((error) => toast.error(errMsg(error)));
  };
  useEffect(() => {
    load();
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/categories", { name });
      toast.success(`${data.name} category added`);
      setName("");
      onChanged(data.name);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (category) => {
    setDeletingId(category.id);
    try {
      await api.delete(`/inventory/categories/${category.id}`);
      toast.success(`${category.name} category deleted`);
      onChanged();
      load();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setDeletingId("");
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="inventory-manage-categories-dialog">
        <DialogHeader>
          <DialogTitle>Manage inventory categories</DialogTitle>
          <DialogDescription>
            Add categories here. Categories currently used by inventory items are protected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="inventory-add-category-input">Category name</Label>
          <div className="flex gap-2">
            <Input
              id="inventory-add-category-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Glassware"
              data-testid="inventory-add-category-input"
            />
            <Button
              disabled={saving || !name.trim()}
              onClick={save}
              data-testid="inventory-add-category-save-button"
            >
              {saving ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>
        <div className="max-h-64 divide-y overflow-y-auto border" data-testid="inventory-category-list">
          {categories.map((category) => (
            <div
              key={category.name}
              className="flex items-center justify-between gap-3 p-3"
              data-testid={`inventory-category-manager-row-${category.id || category.name}`}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{category.name}</p>
                <p className="text-xs text-muted-foreground">
                  {category.item_count
                    ? `Used by ${category.item_count} inventory item${category.item_count === 1 ? "" : "s"}`
                    : category.id
                      ? "Unused custom category"
                      : "Existing inventory category"}
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                disabled={!category.can_delete || deletingId === category.id}
                title={category.can_delete ? "Delete category" : "Category is in use"}
                onClick={() => remove(category)}
                data-testid={`inventory-category-delete-${category.id || category.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="inventory-manage-categories-close-button"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryField({ value, categories, onChange }) {
  const [creating, setCreating] = useState(false);
  const customCategory = creating || (value && !categories.includes(value));
  const selected = customCategory ? NEW_CATEGORY : value || NONE;
  return (
    <div className="space-y-2">
      <Select
        value={selected}
        onValueChange={(nextValue) => {
          if (nextValue === NEW_CATEGORY) {
            setCreating(true);
            onChange("");
            return;
          }
          setCreating(false);
          onChange(nextValue === NONE ? "" : nextValue);
        }}
      >
        <SelectTrigger data-testid="inventory-category-select">
          <SelectValue placeholder="Choose category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>No category</SelectItem>
          {categories.map((category) => (
            <SelectItem key={category} value={category}>
              {category}
            </SelectItem>
          ))}
          <SelectItem value={NEW_CATEGORY}>Add a new category…</SelectItem>
        </SelectContent>
      </Select>
      {customCategory && (
        <Input
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder="New category name"
          data-testid="inventory-new-category-input"
        />
      )}
    </div>
  );
}

function LocationField({ value, rooms, onChange }) {
  if (!rooms.length)
    return (
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Add labs & storage areas to pick from a list"
        data-testid="inventory-field-location"
      />
    );
  const custom = value && !rooms.some((r) => r.name === value);
  const opts = (kind) =>
    rooms
      .filter((r) => r.kind === kind)
      .map((r) => (
        <SelectItem key={r.id} value={r.name}>
          {r.name}
        </SelectItem>
      ));
  return (
    <Select
      value={value || NONE}
      onValueChange={(v) => onChange(v === NONE ? "" : v)}
    >
      <SelectTrigger data-testid="inventory-field-location">
        <SelectValue placeholder="Choose location" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>Not set</SelectItem>
        {custom && <SelectItem value={value}>{value} (not in list)</SelectItem>}
        <SelectGroup>
          <SelectLabel>Storage areas</SelectLabel>
          {opts("storage")}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Labs</SelectLabel>
          {opts("lab")}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function Editor({ item, categories, onClose, onSaved }) {
  const [f, setF] = useState({
    min_stock: 5,
    date_added: new Date().toISOString().slice(0, 10),
    ...(item || {}),
  });
  const [rooms, setRooms] = useState([]);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [removeSavedPhoto, setRemoveSavedPhoto] = useState(false);
  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data));
  }, []);
  const [count, setCount] = useState("");
  const isChemical = f.category?.trim().toLowerCase() === "chemical";
  const save = async () => {
    try {
      const saved = f.id
        ? await api.put(`/inventory/${f.id}`, f)
        : await api.post("/inventory", f);
      const savedItem = saved.data;
      setF(savedItem);
      if (count !== "") {
        await api.post(`/inventory/${savedItem.id}/verify`, {
          verified_quantity: Number(count),
        });
      }
      if (pendingPhoto) {
        const photoData = new FormData();
        photoData.append("file", pendingPhoto);
        await api.put(`/inventory/${savedItem.id}/photo`, photoData);
      } else if (removeSavedPhoto && item.has_photo) {
        await api.delete(`/inventory/${savedItem.id}/photo`);
      }
      let failedAttachments = 0;
      for (const attachmentFile of pendingAttachments) {
        const attachment = new FormData();
        attachment.append("file", attachmentFile);
        attachment.append("title", attachmentFile.name);
        attachment.append("category", "Equipment manual");
        attachment.append("inventory_id", savedItem.id);
        try {
          await api.post("/documents", attachment);
        } catch {
          failedAttachments += 1;
        }
      }
      toast.success("Saved");
      if (failedAttachments) {
        toast.warning(
          `${failedAttachments} attachment${failedAttachments === 1 ? "" : "s"} couldn't be uploaded.`,
        );
      }
      window.dispatchEvent(new Event("ask-jonny:inventory-updated"));
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="inventory-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle>{item.id ? item.name : "New item"}</DialogTitle>
        </DialogHeader>
        <InventoryPhotoPicker
          item={f}
          pendingFile={pendingPhoto}
          onPendingChange={(file) => {
            setPendingPhoto(file);
            if (file) {
              setRemoveSavedPhoto(false);
            }
          }}
          removeSavedPhoto={removeSavedPhoto}
          onRemoveSavedPhotoChange={setRemoveSavedPhoto}
        />
        <div>
          <Label className="flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Attach documents or manuals
          </Label>
          <Input
            type="file"
            multiple
            accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.webp"
            onChange={(event) => setPendingAttachments(Array.from(event.target.files || []))}
            className="mt-1"
            data-testid="inventory-editor-attachments"
          />
          {pendingAttachments.length > 0 && (
            <p
              className="mt-1 text-xs text-muted-foreground"
              data-testid="inventory-editor-attachment-count"
            >
              {pendingAttachments.length} file{pendingAttachments.length === 1 ? "" : "s"} selected
            </p>
          )}
        </div>
        {item.id && (
          <InventoryDocs
            inventoryId={item.id}
            canEdit
          />
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.filter(
            ([key]) => key !== "hazcard_number" || isChemical,
          ).map(([k, l]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">{l}</Label>
              {k === "category" ? (
                <CategoryField
                  value={f.category}
                  categories={categories}
                  onChange={(value) => setF({ ...f, category: value })}
                />
              ) : k === "location" ? (
                <LocationField
                  value={f.location}
                  rooms={rooms}
                  onChange={(v) => setF({ ...f, location: v })}
                />
              ) : (
                <Input
                  type={
                    k === "youtube_url"
                      ? "url"
                      : k === "date_added"
                        ? "date"
                        : "text"
                  }
                  value={f[k] ?? ""}
                  onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  placeholder={k === "youtube_url" ? "https://youtube.com/..." : ""}
                  maxLength={k === "hazcard_number" ? 100 : undefined}
                  data-testid={`inventory-field-${k}`}
                />
              )}
            </div>
          ))}
        </div>
        <label
          className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3 text-sm"
          data-testid="inventory-conflict-alert-label"
        >
          <Checkbox
            checked={Boolean(f.conflict_alert)}
            onCheckedChange={(value) =>
              setF({ ...f, conflict_alert: Boolean(value) })
            }
            data-testid="inventory-conflict-alert-checkbox"
          />
          <span>
            <span className="block font-medium">Alert on double booking</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              Alert technicians assigned to either booking when this item is needed at the
              same date and period.
            </span>
          </span>
        </label>
        {item.id && (
          <div className="rounded-lg border p-3 space-y-1">
            <Label className="text-xs">
              Physical count (records a verified quantity with today's date)
            </Label>
            <Input
              type="number"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              placeholder={
                item.verified_quantity != null
                  ? `Last verified: ${item.verified_quantity}`
                  : "Not yet verified"
              }
              data-testid="inventory-verify-input"
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} data-testid="inventory-save-button">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StockChangeLog({ onClose }) {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/stock-changes")
      .then((response) => {
        setChanges(response.data);
      })
      .catch((error) => {
        toast.error(errMsg(error));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[85vh] max-w-3xl overflow-y-auto"
        data-testid="stock-change-log-dialog"
      >
        <DialogHeader>
          <DialogTitle>Jonny stock changes</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="stock-change-log-loading"
          >
            Loading changes…
          </p>
        ) : changes.length === 0 ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="stock-change-log-empty"
          >
            No stock changes made through Jonny yet.
          </p>
        ) : (
          <div
            className="divide-y rounded-lg border"
            data-testid="stock-change-log-list"
          >
            {changes.map((change) => (
              <div
                key={change.id}
                className="grid gap-1 px-4 py-3 sm:grid-cols-[1fr_auto]"
                data-testid={`stock-change-log-row-${change.id}`}
              >
                <div>
                  <div
                    className="font-medium"
                    data-testid={`stock-change-log-item-${change.id}`}
                  >
                    {change.inventory_name}
                  </div>
                  <p
                    className="mt-0.5 text-sm text-muted-foreground"
                    data-testid={`stock-change-log-reason-${change.id}`}
                  >
                    {change.reason}
                  </p>
                  <p
                    className="mt-1 text-xs text-muted-foreground"
                    data-testid={`stock-change-log-user-${change.id}`}
                  >
                    {change.changed_by} ·{" "}
                    {new Date(change.created_at).toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                <div
                  className="font-mono text-sm sm:text-right"
                  data-testid={`stock-change-log-quantities-${change.id}`}
                >
                  {change.previous_quantity} → {change.new_quantity}{" "}
                  {change.units}
                  <span className="ml-2 text-destructive">{change.change}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ThumbnailToggle({ checked, onCheckedChange }) {
  return (
    <label className="mb-4 flex w-fit items-center gap-2 text-sm" data-testid="inventory-thumbnail-toggle">
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        data-testid="inventory-thumbnail-switch"
      />
      Show photos
    </label>
  );
}

export default function InventoryPage() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [sortBy, setSortBy] = useState("category");
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkCategory, setBulkCategory] = useState(BULK_CATEGORY);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const [imp, setImp] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [managingCategories, setManagingCategories] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    return api
      .get("/inventory", {
        params: {
          q,
          category: category === ALL_CATEGORIES ? "" : category,
          sort: sortBy,
        },
      })
      .then((r) => setItems(r.data))
      .catch((error) => toast.error(errMsg(error)))
      .finally(() => setLoading(false));
  }, [category, q, sortBy]);
  const loadCategories = useCallback(() => {
    return api
      .get("/inventory/categories")
      .then((response) => setCategories(response.data))
      .catch((error) => toast.error(errMsg(error)));
  }, []);
  const showImportedInventory = () => {
    setQ("");
    setCategory(ALL_CATEGORIES);
    setSortBy("newest");
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);
  useEffect(() => {
    api
      .get("/me/preferences")
      .then(({ data }) => setShowThumbnails(Boolean(data.show_inventory_thumbnails)))
      .catch(() => setShowThumbnails(false));
  }, []);
  const canEdit = can("inventory:write");
  const allSelected = items.length > 0 && selected.length === items.length;
  const toggleSelected = (id) => {
    setSelected((current) => (
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    ));
  };
  const assignCategory = async () => {
    if (bulkCategory === BULK_CATEGORY) {
      return;
    }
    try {
      const { data } = await api.post("/inventory/bulk-category", {
        ids: selected,
        category: bulkCategory,
      });
      toast.success(`${data.updated} item${data.updated === 1 ? "" : "s"} moved to ${data.category}`);
      setSelected([]);
      setBulkCategory(BULK_CATEGORY);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const deleteSelected = async () => {
    try {
      const { data } = await api.post("/inventory/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} item${data.deleted === 1 ? "" : "s"} deleted`);
      setSelected([]);
      setConfirmBulkDelete(false);
      load();
      loadCategories();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  useEffect(() => {
    setSelected((current) => current.filter((id) => items.some((item) => item.id === id)));
  }, [items]);
  const toggleThumbnails = async (nextValue) => {
    const previousValue = showThumbnails;
    setShowThumbnails(nextValue);
    try {
      await api.put("/me/preferences", { show_inventory_thumbnails: nextValue });
    } catch (error) {
      setShowThumbnails(previousValue);
      toast.error(errMsg(error));
    }
  };

  return (
    <Page wide>
      <PageHeader
        title="Equipment & chemicals"
        subtitle="Recorded stock vs. last physical count"
      >
        <PrintButton label="Print current list" testId="inventory-print-button" />
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => setShowLog(true)}
            data-testid="inventory-stock-change-log-button"
          >
            <History className="h-4 w-4 mr-2" />
            Jonny change log
          </Button>
        )}
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => setImp(true)}
            data-testid="inventory-import-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
        )}
        {canEdit && (
          <Button asChild variant="outline" data-testid="inventory-download-template-button">
            <a href="/ask-jonny-inventory-import-template.xlsx" download>
              <Download className="mr-2 h-4 w-4" />
              Download template
            </a>
          </Button>
        )}
        {canEdit && (
          <Button
            onClick={() => setEdit({})}
            data-testid="inventory-new-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add item
          </Button>
        )}
      </PageHeader>
      {can("booking:prep") && <InventoryAttentionPanel onOpenItem={setView} />}
      <div className="relative mb-5">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search — e.g. microscope, beaker, acid"
          className="pl-9"
          data-testid="inventory-search-input"
        />
      </div>
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-64 max-w-full">
          <Label className="mb-1.5 block text-xs">Category</Label>
          <Select
            value={category}
            onValueChange={(nextValue) => {
              if (nextValue === MANAGE_CATEGORIES) {
                setManagingCategories(true);
                return;
              }
              setCategory(nextValue);
            }}
          >
            <SelectTrigger data-testid="inventory-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>All categories</SelectItem>
              {categories.map((categoryName) => (
                <SelectItem key={categoryName} value={categoryName}>
                  {categoryName}
                </SelectItem>
              ))}
              {canEdit && (
                <SelectItem
                  value={MANAGE_CATEGORIES}
                  data-testid="inventory-manage-categories-option"
                >
                  Add / delete categories…
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => setManagingCategories(true)}
            data-testid="inventory-manage-categories-button"
          >
            <Settings2 className="mr-2 h-4 w-4" />
            Manage categories
          </Button>
        )}
        <div className="w-48">
          <Label className="mb-1.5 block text-xs">Sort items</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="inventory-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`inventory-sort-option-${option.value}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => setShowDuplicates(true)}
            data-testid="inventory-duplicates-button"
          >
            <Copy className="mr-2 h-4 w-4" />
            Possible duplicates
          </Button>
        )}
      </div>
      <ThumbnailToggle
        checked={showThumbnails}
        onCheckedChange={toggleThumbnails}
      />
      {loading ? (
        <div
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="inventory-loading"
        >
          Loading inventory…
        </div>
      ) : items.length === 0 ? (
        <Empty>No items found.</Empty>
      ) : (
        <>
        {canEdit && (
          <div
            className="mb-3 flex flex-wrap items-center gap-3"
            data-testid="inventory-selection-bar"
          >
            <label className="flex items-center gap-2 text-sm" data-testid="inventory-select-all-label">
              <Checkbox
                checked={allSelected}
                onCheckedChange={() => setSelected(allSelected ? [] : items.map((item) => item.id))}
                data-testid="inventory-select-all"
              />
              Select all
            </label>
            {selected.length > 0 && (
              <>
                <span className="text-sm text-muted-foreground" data-testid="inventory-selected-count">
                  {selected.length} selected
                </span>
                <Select value={bulkCategory} onValueChange={setBulkCategory}>
                  <SelectTrigger className="w-52" data-testid="inventory-bulk-category-select">
                    <SelectValue placeholder="Assign category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={BULK_CATEGORY}>Assign to category…</SelectItem>
                    {categories.map((categoryName) => (
                      <SelectItem key={categoryName} value={categoryName}>
                        {categoryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={assignCategory}
                  disabled={bulkCategory === BULK_CATEGORY}
                  data-testid="inventory-bulk-category-save"
                >
                  Move selected
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setConfirmBulkDelete(true)}
                  data-testid="inventory-bulk-delete-button"
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete selected
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected([])}
                  data-testid="inventory-clear-selection"
                >
                  Clear
                </Button>
              </>
            )}
          </div>
        )}
        <div className="overflow-x-auto rounded-xl border bg-card">
          <table
            className="w-full text-sm min-w-[760px]"
            data-testid="inventory-table"
          >
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                {canEdit && <th className="w-10 p-3" />}
                <th className="p-3">Item</th>
                <th className="p-3">Category</th>
                <th className="p-3 text-right">Recorded</th>
                <th className="p-3 text-right">Verified count</th>
                <th className="p-3">Location</th>
                <th className="p-3">Condition</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const low =
                  i.min_stock != null &&
                  i.quantity != null &&
                  i.quantity <= i.min_stock;
                return (
                  <tr
                    key={i.id}
                    onClick={() => setView(i)}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                    data-testid={`inventory-row-${i.id}`}
                  >
                    {canEdit && (
                      <td className="p-3" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={selected.includes(i.id)}
                          onCheckedChange={() => toggleSelected(i.id)}
                          aria-label={`Select ${i.name}`}
                          data-testid={`inventory-select-${i.id}`}
                        />
                      </td>
                    )}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {showThumbnails && <InventoryPhoto item={i} compact />}
                        <div>
                          <div className="flex items-center gap-1.5 font-medium">
                            {i.name}
                            {i.hazard_info && i.hazard_info !== "None" && (
                              <ShieldAlert
                                className="h-3.5 w-3.5 text-amber-500"
                                title={i.hazard_info}
                              />
                            )}
                          </div>
                          {i.hazard_info && (
                            <div className="text-[11px] text-muted-foreground line-clamp-1">
                              {i.hazard_info}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-muted-foreground">{i.category}</td>
                    <td
                      className={`p-3 text-right font-mono ${low ? "text-destructive font-semibold" : ""}`}
                    >
                      {i.quantity}{" "}
                      <span className="text-muted-foreground text-xs">
                        {i.units}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono text-xs">
                      {i.verified_quantity != null ? (
                        <>
                          {i.verified_quantity}
                          <div className="text-[10px] text-muted-foreground">
                            {new Date(i.verified_at).toLocaleDateString(
                              "en-GB",
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">
                          not verified
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs">{i.location}</td>
                    <td className="p-3 text-xs">{i.condition}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      {edit && (
        <Editor
          item={edit}
          categories={categories}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
            loadCategories();
          }}
        />
      )}
      {view && (
        <InventoryDetailsDialog
          item={view}
          canEdit={canEdit}
          documents={<InventoryDocs inventoryId={view.id} canEdit={canEdit} />}
          onClose={() => setView(null)}
          onEdit={() => {
            setView(null);
            setEdit(view);
          }}
        />
      )}
      {showLog && <StockChangeLog onClose={() => setShowLog(false)} />}
      <ImportDialog
        kind="inventory"
        open={imp}
        onOpenChange={setImp}
        onDone={showImportedInventory}
      />
      {managingCategories && (
        <ManageCategoriesDialog
          onClose={() => setManagingCategories(false)}
          onChanged={(newCategory) => {
            loadCategories();
            if (newCategory) setCategory(newCategory);
            if (!newCategory && category !== ALL_CATEGORIES) setCategory(ALL_CATEGORIES);
          }}
        />
      )}
      {showDuplicates && (
        <InventoryDuplicatesDialog
          onClose={() => setShowDuplicates(false)}
          onChanged={() => {
            load();
            loadCategories();
          }}
        />
      )}
      <AlertDialog
        open={confirmBulkDelete}
        onOpenChange={(open) => !open && setConfirmBulkDelete(false)}
      >
        <AlertDialogContent data-testid="inventory-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selected.length} selected item{selected.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected inventory records and their linked item references.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="inventory-bulk-delete-cancel">Keep items</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteSelected}
              data-testid="inventory-bulk-delete-confirm"
            >
              Delete selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
